import { useState } from 'react';
import { Search } from '@/shared/components/Search';
import {
  AccountActionButton,
  UserLanguageSelectInput,
} from '@/shared/components';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowTopRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import TransferIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import { useMediaQuery } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { HEADER_SEARCH_ENABLED } from '@/shared/config';
import { AuthorizedLayoutMenuButton } from './AuthorizedLayoutMenuButton';
import { SidebarNavigationMobile } from './sidebar';
import { UserBalanceDropdown } from './user-balance-dropdown';
import { SettingsIcon } from 'lucide-react';

type Props = {
  onDepositClick: () => void;
  onWithdrawClick: () => void;
  onTransferClick: () => void;
  onSettingsClick: () => void;
  buttonsVisibility: {
    deposit: boolean;
    withdraw: boolean;
    transfer: boolean;
  };
};

function AuthorizedLayoutHeader({
  onDepositClick,
  onWithdrawClick,
  onTransferClick,
  onSettingsClick,
  buttonsVisibility,
}: Props) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 919px)');
  const isDesktop = useMediaQuery('(min-width: 480px)');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { deposit, withdraw, transfer } = buttonsVisibility;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <div className="fixed top-0 w-screen h-[8.6rem] z-10 flex justify-between items-center">
        {/* Block to create a fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, var(--secondary-bg) 0%, var(--secondary-bg) 55%, transparent 100%)',
          }}
        />

        {/* Block to create a blur effect */}
        <div
          className="absolute inset-0"
          style={{
            backdropFilter: 'blur(16px)',
            maskImage:
              'linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%)',
          }}
        />

        <header className="relative flex-1 h-[8.6rem] flex flex-row items-center gap-[1.2rem] px-[2.4rem] tablet:pr-[4.8rem] tablet:pl-[1.2rem]">
          <UserBalanceDropdown />

          <div className="flex-1">
            {HEADER_SEARCH_ENABLED && (
              <Search
                label={t('personalAccount.header.searchPlaceholder')}
                value=""
                onChange={() => {}}
              />
            )}
          </div>

          <div className="flex items-center gap-[1.2rem]">
            {!isMobile && deposit && (
              <AccountActionButton
                icon={ArrowDownIcon}
                label={t('header.actions.deposit')}
                onClick={() => onDepositClick()}
              />
            )}

            {!isMobile && withdraw && (
              <AccountActionButton
                icon={ArrowTopRightIcon}
                label={t('header.actions.withdraw')}
                onClick={() => onWithdrawClick()}
              />
            )}

            {!isMobile && transfer && (
              <AccountActionButton
                icon={TransferIcon}
                label={t('header.actions.transfer')}
                onClick={() => onTransferClick()}
              />
            )}

            {isDesktop && <UserLanguageSelectInput />}

            {!isMobile && (
              <AccountActionButton
                icon={SettingsIcon}
                label={''}
                onClick={() => onSettingsClick()}
                className="dark:bg-background dark:text-primary min-w-[4.2rem] max-w-[4.2rem] p-0"
              />
            )}
          </div>

          <AuthorizedLayoutMenuButton
            isMenuOpen={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="tablet:hidden z-[60]"
          />
        </header>
      </div>

      {isMobileMenuOpen && (
        <div className="fixed top-[2.2rem] right-[2.4rem] z-[60] tablet:hidden">
          <AuthorizedLayoutMenuButton
            isMenuOpen={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
        </div>
      )}

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 tablet:hidden">
          <div
            className="absolute inset-0 bg-primary/50"
            // Remove if click on faded area should not close the menu
            onClick={closeMobileMenu}
          />

          <div className="relative top-[8rem] w-full md:w-[36rem] px-[2.4rem]">
            <SidebarNavigationMobile onClose={closeMobileMenu} />
          </div>
        </div>
      )}
    </>
  );
}

export { AuthorizedLayoutHeader };
